import React from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TypedMemo } from '../../common/types';
import { SMLLink } from '../components/SMLLink/SMLLink';
import { SMLNavItem } from './smlNavigation';
import { SMLNavBadge } from './hooks/useNavBadges';
import { NavBadge } from './NavBadge';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole sidebar; each part uses its own slice of it */
import styles from '../SMLSideBar.module.scss';
/* eslint-enable css-modules/no-unused-class */

/**
 * The rail tooltip is fixed to the window (the list scrolls, which would clip it), so the row
 * hands it its own vertical centre.
 */
export function placeRailTip(event: React.SyntheticEvent<HTMLElement>): void {
  const rect = event.currentTarget.getBoundingClientRect();
  event.currentTarget.style.setProperty('--tip-y', `${rect.top + rect.height / 2}px`);
}

interface SidebarNavItemProps {
  item: SMLNavItem;
  isActive: boolean;
  badge?: SMLNavBadge;
  /** The rail tooltip: "Discover · Charts". The item label alone outside a group. */
  tooltip?: string;
  params?: Record<string, string>;
  /** Classes on the list item, for the group and rail rules. */
  className?: string;
  onNavigate?: () => void;
}

/** One destination row of the sidebar and the rail (design a4 states). */
const SidebarNavItemComponent: React.FC<SidebarNavItemProps> = ({
  item,
  isActive,
  badge,
  tooltip,
  params,
  className,
  onNavigate,
}) => {
  const intl = useIntl();
  const label = intl.formatMessage(item.label);

  return (
    <li className={clsx(styles.itemRow, className)} onMouseEnter={placeRailTip} onFocus={placeRailTip}>
      <SMLLink
        to={item.route}
        params={params}
        className={clsx(styles.navItem, isActive && styles.navItemActive)}
        ariaCurrent={isActive ? 'page' : undefined}
        onNavigate={onNavigate}
      >
        <FontAwesomeIcon icon={item.icon} className={styles.icon} fixedWidth />
        <span className={styles.label}>{label}</span>
        {badge !== undefined && <NavBadge badge={badge} className={styles.badge} />}
        <span className={styles.tip} aria-hidden="true">
          {tooltip ?? label}
        </span>
      </SMLLink>
    </li>
  );
};

export const SidebarNavItem = TypedMemo(SidebarNavItemComponent);
